import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Box,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  ArrowBack,
  Edit,
  Save,
  Cancel,
  Add,
  Delete,
} from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import {
  useApiGet,
  useApiSend,
  getApiErrorMessage,
} from '../../utils/httpClientTs';
import {
  getFundDetail,
  patchFund,
  createFundNote,
  patchFundNote,
  deleteFundNote,
  createFundPosition,
  patchFundPosition,
  deleteFundPosition,
} from '../../endpoints/financeFunds';
import type {
  FundDetailResponse,
  PatchFundRequest,
  PatchFundPositionRequest,
} from '../../types/financeFunds';
import PositionDisplay from '../../Components/PositionDisplay';
import UserSearchModal, {
  User as UserSearchUser,
} from '../../Components/UserSearchModal';
import AlertItem from '../../Components/AlertItem';

interface PositionSaveData {
  id: string;
  positionId?: string;
  committeeId?: string;
  action: 'newAppointment' | 'editDates' | 'removeRecord';
  userId?: string;
  startDate?: string | null;
  endDate?: string | null;
}

const FundDetail: React.FC = () => {
  const { id: fundId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [editingFund, setEditingFund] = useState(false);
  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [accountId, setAccountId] = useState('');
  const [description, setDescription] = useState('');
  const [canDonate, setCanDonate] = useState(false);
  const [sponsorCode, setSponsorCode] = useState('');
  const [typeId, setTypeId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [establishedBy, setEstablishedBy] = useState('');
  const [establishedDate, setEstablishedDate] = useState<Dayjs | null>(null);

  const [newNote, setNewNote] = useState('');
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [editNoteId, setEditNoteId] = useState<string | null>(null);
  const [editNoteText, setEditNoteText] = useState('');

  const [addChair, setAddChair] = useState(false);
  const [chairMember, setChairMember] = useState<UserSearchUser | null>(null);
  const [chairRoleId, setChairRoleId] = useState('');
  const [chairStart, setChairStart] = useState<Dayjs | null>(dayjs());
  const [chairEnd, setChairEnd] = useState<Dayjs | null>(null);
  const [openUserSearch, setOpenUserSearch] = useState(false);

  const [fundError, setFundError] = useState<string | null>(null);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [chairError, setChairError] = useState<string | null>(null);
  const [positionSaving, setPositionSaving] = useState(false);
  const [positionError, setPositionError] = useState<string | null>(null);

  const {
    data,
    isLoading,
    isError,
    error: loadError,
  } = useApiGet<FundDetailResponse, string>(
    ['fund-detail', fundId!],
    getFundDetail,
    { enabled: !!fundId, retry: 1 },
    fundId!
  );

  const syncFromData = useCallback((d: FundDetailResponse) => {
    setName(d.name || '');
    setShortName(d.short_name || '');
    setAccountId(d.account_id || '');
    setDescription(d.description || '');
    setCanDonate(d.can_donate === 1);
    setSponsorCode(d.sponser_chapter_code || '');
    setTypeId(d.type_id || '');
    setCategoryId(d.category_id || '');
    setEstablishedBy(d.established_by || '');
    setEstablishedDate(d.established_date ? dayjs(d.established_date) : null);
  }, []);

  useEffect(() => {
    if (data && !editingFund) {
      syncFromData(data);
    }
  }, [data, editingFund, syncFromData]);

  const { mutate: patchFundMut } = useApiSend(
    (p: { id: string; body: PatchFundRequest }) => patchFund(p.id, p.body),
    () => {
      setEditingFund(false);
      setFundError(null);
    },
    (e: Error) => setFundError(getApiErrorMessage(e)),
    [['fund-detail', fundId]]
  );

  const { mutate: addNoteMut } = useApiSend(
    (p: { id: string; note: string }) => createFundNote(p.id, { note: p.note }),
    () => {
      setNewNote('');
      setNoteError(null);
    },
    (e: Error) => setNoteError(getApiErrorMessage(e)),
    [['fund-detail', fundId]]
  );

  const { mutate: patchNoteMut } = useApiSend(
    (p: { id: string; noteId: string; note: string }) =>
      patchFundNote(p.id, p.noteId, { note: p.note }),
    () => {
      setNoteDialogOpen(false);
      setEditNoteId(null);
      setNoteError(null);
    },
    (e: Error) => setNoteError(getApiErrorMessage(e)),
    [['fund-detail', fundId]]
  );

  const { mutate: deleteNoteMut } = useApiSend(
    (p: { id: string; noteId: string }) => deleteFundNote(p.id, p.noteId),
    () => {
      setNoteError(null);
    },
    (e: Error) => setNoteError(getApiErrorMessage(e)),
    [['fund-detail', fundId]]
  );

  const { mutate: addPositionMut } = useApiSend(
    (p: {
      id: string;
      body: {
        user_id: string;
        role_id: string;
        start_date: string;
        end_date?: string | null;
      };
    }) => createFundPosition(p.id, p.body),
    () => {
      setAddChair(false);
      setChairMember(null);
      setChairRoleId('');
      setChairStart(dayjs());
      setChairEnd(null);
      setChairError(null);
    },
    (e: Error) => setChairError(getApiErrorMessage(e)),
    [['fund-detail', fundId]]
  );

  const { mutate: patchPositionMut } = useApiSend(
    (p: { id: string; memberId: string; body: PatchFundPositionRequest }) =>
      patchFundPosition(p.id, p.memberId, p.body),
    () => {
      setPositionSaving(false);
      setPositionError(null);
    },
    (e: Error) => {
      setPositionSaving(false);
      setPositionError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const { mutate: deletePositionMut } = useApiSend(
    (p: { id: string; memberId: string }) =>
      deleteFundPosition(p.id, p.memberId),
    () => {
      setPositionSaving(false);
      setPositionError(null);
    },
    (e: Error) => {
      setPositionSaving(false);
      setPositionError(getApiErrorMessage(e));
    },
    [['fund-detail', fundId]]
  );

  const canManage = data?.can_manage === true;

  const handleSaveFund = () => {
    if (!fundId) return;
    const body: PatchFundRequest = {
      name,
      short_name: shortName,
      account_id: accountId,
      description,
      can_donate: canDonate ? 1 : 0,
      sponser_chapter_code: sponsorCode,
      type_id: typeId || null,
      category_id: categoryId || null,
      established_by: establishedBy,
      // Empty string clears the date; JSON null is skipped by the Go *string binding.
      established_date: establishedDate
        ? establishedDate.format('YYYY-MM-DD')
        : '',
    };
    patchFundMut({ id: fundId, body });
  };

  const handleAddNote = () => {
    if (!fundId || !newNote.trim()) return;
    addNoteMut({ id: fundId, note: newNote.trim() });
  };

  const openEditNote = (noteId: string, text: string) => {
    setEditNoteId(noteId);
    setEditNoteText(text);
    setNoteDialogOpen(true);
  };

  const handleSaveNote = () => {
    if (!fundId || !editNoteId) return;
    patchNoteMut({ id: fundId, noteId: editNoteId, note: editNoteText.trim() });
  };

  const handleDeleteNote = (noteId: string) => {
    if (!fundId) return;
    if (!window.confirm('Delete this note?')) return;
    deleteNoteMut({ id: fundId, noteId });
  };

  const handleSaveAddChair = () => {
    if (!fundId || !chairMember || !chairRoleId || !chairStart) {
      setChairError('Member, role, and start date are required.');
      return;
    }
    setChairError(null);
    addPositionMut({
      id: fundId,
      body: {
        user_id: chairMember.id,
        role_id: chairRoleId,
        start_date: chairStart.format('YYYY-MM-DD'),
        end_date: chairEnd ? chairEnd.format('YYYY-MM-DD') : undefined,
      },
    });
  };

  const onSavePosition = (save: PositionSaveData) => {
    if (!fundId) return;
    setPositionSaving(true);
    setPositionError(null);
    if (save.action === 'removeRecord') {
      deletePositionMut({ id: fundId, memberId: save.id });
      return;
    }
    if (save.action === 'editDates') {
      const body: PatchFundPositionRequest = {
        start_date: save.startDate || undefined,
        end_date:
          save.endDate === null || save.endDate === undefined
            ? ''
            : save.endDate,
      };
      if (save.positionId) {
        body.role_id = save.positionId;
      }
      patchPositionMut({ id: fundId, memberId: save.id, body });
    }
  };

  if (!fundId) {
    return null;
  }

  if (isLoading) {
    return (
      <Grid container paddingLeft={2} paddingRight={2} sx={{ pt: 3 }}>
        <Typography>Loading…</Typography>
      </Grid>
    );
  }

  if (isError || !data) {
    return (
      <Grid container paddingLeft={2} paddingRight={2} sx={{ pt: 3 }}>
        <Grid item xs={12}>
          <AlertItem
            severity="error"
            description={getApiErrorMessage(loadError)}
            onClose={() => {}}
            keepOpen
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/finance/funds')}
          >
            Back to funds
          </Button>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container paddingLeft={2} paddingRight={2} spacing={2} sx={{ pb: 4 }}>
      <Grid item xs={12}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/finance/funds')}
          sx={{ mb: 1 }}
        >
          Back to funds
        </Button>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title="Fund" subheader={data.account_id || data.id} />
          <CardContent>
            {fundError && (
              <Box sx={{ mb: 2 }}>
                <AlertItem
                  severity="error"
                  description={fundError}
                  onClose={() => setFundError(null)}
                  keepOpen
                />
              </Box>
            )}
            <Grid container spacing={2}>
              {editingFund ? (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Short name"
                      value={shortName}
                      onChange={(e) => setShortName(e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Account"
                      value={accountId}
                      onChange={(e) => setAccountId(e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={canDonate}
                          onChange={(e) => setCanDonate(e.target.checked)}
                        />
                      }
                      label="Can donate"
                    />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <b>Name:</b> {data.name || '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <b>Short name:</b> {data.short_name || '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <b>Account:</b> {data.account_id || '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <b>Description:</b> {data.description || '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <b>Can donate:</b> {data.can_donate === 1 ? 'Yes' : 'No'}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
          {canManage && (
            <CardActions>
              {editingFund ? (
                <>
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<Cancel />}
                    onClick={() => {
                      setEditingFund(false);
                      syncFromData(data);
                      setFundError(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<Save />}
                    onClick={handleSaveFund}
                  >
                    Save
                  </Button>
                </>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<Edit />}
                  onClick={() => setEditingFund(true)}
                >
                  Edit
                </Button>
              )}
            </CardActions>
          )}
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title="Chapter & classification" />
          <CardContent>
            <Grid container spacing={2}>
              {editingFund ? (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Sponsor chapter code"
                      value={sponsorCode}
                      onChange={(e) => setSponsorCode(e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Type</InputLabel>
                      <Select
                        label="Type"
                        value={typeId}
                        onChange={(e) => setTypeId(String(e.target.value))}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {data.fund_type_options.map((t) => (
                          <MenuItem key={t.id} value={t.id}>
                            {t.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Category</InputLabel>
                      <Select
                        label="Category"
                        value={categoryId}
                        onChange={(e) => setCategoryId(String(e.target.value))}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {data.fund_category_options.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Established by"
                      value={establishedBy}
                      onChange={(e) => setEstablishedBy(e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label="Established date"
                      value={establishedDate}
                      onChange={(v) => setEstablishedDate(v)}
                      slotProps={{
                        textField: { fullWidth: true, size: 'small' },
                      }}
                    />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <b>Sponsor chapter:</b> {data.sponser_chapter_code || '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <b>Type:</b> {data.type_name || '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <b>Category:</b> {data.category_name || '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <b>Established by:</b> {data.established_by || '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2">
                      <b>Established date:</b> {data.established_date || '—'}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title="Chair / Co-Chair" />
          <CardContent>
            {positionError && (
              <Box sx={{ mb: 2 }}>
                <AlertItem
                  severity="error"
                  description={positionError}
                  onClose={() => setPositionError(null)}
                  keepOpen
                />
              </Box>
            )}
            {chairError && (
              <Box sx={{ mb: 2 }}>
                <AlertItem
                  severity="error"
                  description={chairError}
                  onClose={() => setChairError(null)}
                  keepOpen
                />
              </Box>
            )}
            {data.positions.map((p) => (
              <PositionDisplay
                key={p.id}
                id={p.id}
                position={p.position || 'Role'}
                positionId={p.position_id}
                committeeId={undefined}
                name={p.name}
                startDate={p.start_date || null}
                endDate={p.end_date ?? null}
                canEdit={canManage}
                isSaving={positionSaving}
                errorSavingText={positionError || undefined}
                onSave={onSavePosition}
                userId={p.user_id}
                pageFrom="finance-fund-chairs"
                canMessage={false}
                chairRoleOptions={data.chair_roles}
              />
            ))}
            {data.positions.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No chair positions recorded yet.
              </Typography>
            )}
            {canManage && (
              <>
                {!addChair ? (
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={() => {
                      setAddChair(true);
                      setOpenUserSearch(true);
                    }}
                  >
                    Add chair
                  </Button>
                ) : (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      New chair
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {chairMember?.full_name ||
                        [chairMember?.first_name, chairMember?.last_name]
                          .filter(Boolean)
                          .join(' ') ||
                        '—'}
                      <Button
                        size="small"
                        onClick={() => setOpenUserSearch(true)}
                        sx={{ ml: 1 }}
                      >
                        Select member
                      </Button>
                    </Typography>
                    <FormControl
                      fullWidth
                      size="small"
                      sx={{ mt: 1, maxWidth: 400 }}
                    >
                      <InputLabel>Role</InputLabel>
                      <Select
                        label="Role"
                        value={chairRoleId}
                        onChange={(e) => setChairRoleId(String(e.target.value))}
                      >
                        {data.chair_roles.map((r) => (
                          <MenuItem key={r.id} value={r.id}>
                            {r.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Box sx={{ mt: 2, maxWidth: 400 }}>
                      <DatePicker
                        label="Start date"
                        value={chairStart}
                        onChange={(v) => setChairStart(v)}
                        slotProps={{
                          textField: { fullWidth: true, size: 'small' },
                        }}
                      />
                    </Box>
                    <Box sx={{ mt: 2, maxWidth: 400 }}>
                      <DatePicker
                        label="End date (optional)"
                        value={chairEnd}
                        onChange={(v) => setChairEnd(v)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            helperText: 'Leave blank if current',
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={handleSaveAddChair}
                        sx={{ mr: 1 }}
                      >
                        Save
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setAddChair(false);
                          setChairMember(null);
                          setChairRoleId('');
                          setChairError(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
          <CardHeader title="Fund notes" />
          <CardContent>
            {noteError && (
              <Box sx={{ mb: 2 }}>
                <AlertItem
                  severity="error"
                  description={noteError}
                  onClose={() => setNoteError(null)}
                  keepOpen
                />
              </Box>
            )}
            {data.notes.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No notes yet.
              </Typography>
            )}
            {data.notes.map((n) => (
              <Box key={n.id} sx={{ mb: 2 }}>
                <Typography variant="body2">{n.note}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {n.created_by_name || n.created_by_id}
                  {n.created_at
                    ? ` · ${dayjs(n.created_at).format('MM/DD/YYYY h:mm A')}`
                    : ''}
                </Typography>
                {canManage && (
                  <Box sx={{ mt: 0.5 }}>
                    <IconButton
                      size="small"
                      aria-label="Edit note"
                      onClick={() => openEditNote(n.id, n.note)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label="Delete note"
                      onClick={() => handleDeleteNote(n.id)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                )}
                <Divider sx={{ mt: 1 }} />
              </Box>
            ))}
            {canManage && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  label="New note"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                />
                <Button
                  sx={{ mt: 1 }}
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddNote}
                  disabled={!newNote.trim()}
                >
                  Add note
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      <UserSearchModal
        modalOpen={openUserSearch}
        setModalOpen={setOpenUserSearch}
        setUser={(u: UserSearchUser) => {
          setChairMember(u);
          setOpenUserSearch(false);
        }}
        allowSetToMe={false}
        userObjectRet={true}
        chapter={null}
        spd={null}
        activeMembers={true}
        statusFilter="active"
      />

      <Dialog
        open={noteDialogOpen}
        onClose={() => setNoteDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit note</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Note"
            fullWidth
            multiline
            minRows={3}
            value={editNoteText}
            onChange={(e) => setEditNoteText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveNote}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default FundDetail;
